import type {
  ChatMessage,
  ChatStreamEvent,
  SourceCitation,
  WidgetConfig,
  WidgetConfigResponse,
  QuickAction,
} from "@clearmind/shared";
import { DEFAULT_WIDGET_CONFIG } from "@clearmind/shared";
import { styles } from "./styles";

const SCRIPT_ATTR = "data-clearmind-chat";
const SESSION_STORAGE_KEY = "clearmind_chat_session";

const ICONS = {
  chat: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  close: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`,
  send: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>`,
  external: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>`,
  thumbUp: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/></svg>`,
  thumbDown: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/></svg>`,
} as const;

function getScriptElement(): HTMLScriptElement | null {
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    `script[${SCRIPT_ATTR}], script[data-site-key]`
  );
  return scripts.length ? scripts[scripts.length - 1] : null;
}

function parseConfig(): WidgetConfig {
  const el = getScriptElement();
  const siteKey = el?.dataset.siteKey ?? "demo";
  const apiUrl =
    el?.dataset.apiUrl ??
    (import.meta.env?.DEV ? "http://localhost:8080" : "https://api.chat.clearmind.com");

  return {
    siteKey,
    apiUrl: apiUrl.replace(/\/$/, ""),
    position: (el?.dataset.position as WidgetConfig["position"]) ?? DEFAULT_WIDGET_CONFIG.position,
    theme: (el?.dataset.theme as WidgetConfig["theme"]) ?? DEFAULT_WIDGET_CONFIG.theme,
    primaryColor: el?.dataset.primaryColor ?? DEFAULT_WIDGET_CONFIG.primaryColor,
    greeting: el?.dataset.greeting ?? DEFAULT_WIDGET_CONFIG.greeting,
    disclaimer: el?.dataset.disclaimer ?? DEFAULT_WIDGET_CONFIG.disclaimer,
    locale: el?.dataset.locale ?? DEFAULT_WIDGET_CONFIG.locale,
  };
}

function uuid(): string {
  return crypto.randomUUID();
}

function getOrCreateSessionId(): string {
  try {
    const existing = localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing && existing.length >= 8) return existing;
    const id = uuid();
    localStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    return uuid();
  }
}

export class ClearMindChatWidget {
  private config: WidgetConfig;
  private sessionId = getOrCreateSessionId();
  private host: HTMLElement | null = null;
  private shadow: ShadowRoot | null = null;
  private panelOpen = false;
  private messages: ChatMessage[] = [];
  private streaming = false;
  private streamingMessageId: string | null = null;
  private mode: "ai_active" | "waiting_for_human" | "human_active" | "resolved" =
    "ai_active";
  private leadSubmitting = false;
  private leadSaved = false;
  private leadError = "";
  private quickActions: QuickAction[] = [];
  private pollTimer: number | null = null;

  constructor(config?: Partial<WidgetConfig>) {
    const base = parseConfig();
    this.config = { ...base, ...config };
  }

  mount(): void {
    if (this.host) return;

    this.host = document.createElement("div");
    this.host.id = "clearmind-chat-root";
    this.host.setAttribute("data-site-key", this.config.siteKey);
    document.body.appendChild(this.host);

    this.shadow = this.host.attachShadow({ mode: "open" });
    this.renderShell();
    void this.loadRemoteConfig();
  }

  private async loadRemoteConfig(): Promise<void> {
    try {
      const res = await fetch(
        `${this.config.apiUrl}/api/v1/widget/config?site_key=${encodeURIComponent(this.config.siteKey)}`
      );
      if (!res.ok) return;
      const data = (await res.json()) as WidgetConfigResponse;
      this.config.greeting = data.greeting || this.config.greeting;
      this.config.disclaimer = data.disclaimer || this.config.disclaimer;
      if (data.theme_defaults?.primary_color) {
        this.config.primaryColor = data.theme_defaults.primary_color;
      }
      this.quickActions = data.quick_actions ?? [];
      this.renderShell();
    } catch {
      // offline / local dev — use defaults
    }
  }

  private renderShell(): void {
    if (!this.shadow) return;

    const posClass =
      this.config.position === "bottom-left" ? "cm-pos-left" : "cm-pos-right";
    const openClass = this.panelOpen ? "cm-open" : "";
    const panelVisibleClass = this.panelOpen ? "cm-visible" : "";

    this.shadow.innerHTML = `
      <style>${styles(this.config.primaryColor)}</style>
      <div class="cm-root ${posClass} ${openClass}" data-theme="${this.config.theme}">
        <button class="cm-launcher" aria-label="Open chat" type="button">
          ${ICONS.chat}
        </button>
        <div class="cm-panel ${panelVisibleClass}" role="dialog" aria-label="Clearmind support chat" aria-modal="true">
          <header class="cm-header">
            <div class="cm-header-brand">
              <div class="cm-logo" aria-hidden="true">CM</div>
              <div class="cm-header-text">
                <strong class="cm-title">Clearmind Support</strong>
                <span class="cm-subtitle">
                  <span class="cm-status-dot" aria-hidden="true"></span>
                  ${escapeHtml(this.statusLabel())}
                </span>
              </div>
            </div>
            <button class="cm-close" type="button" aria-label="Close chat">${ICONS.close}</button>
          </header>
          <div class="cm-messages" aria-live="polite"></div>
          ${this.renderLeadCapture()}
          <div class="cm-quick-actions"></div>
          <footer class="cm-footer">
            <form class="cm-form">
              <div class="cm-input-wrap">
                <input class="cm-input" type="text" placeholder="Ask about products, fees, or getting started…" autocomplete="off" />
              </div>
              <button class="cm-send" type="submit" aria-label="Send message" ${this.streaming ? "disabled" : ""}>
                ${ICONS.send}
              </button>
            </form>
            <p class="cm-disclaimer">${escapeHtml(this.config.disclaimer)}</p>
          </footer>
        </div>
      </div>
    `;

    this.bindEvents();
    this.renderMessages();
    this.renderQuickActions();
  }

  private bindEvents(): void {
    if (!this.shadow) return;

    const launcher = this.shadow.querySelector(".cm-launcher");
    const closeBtn = this.shadow.querySelector(".cm-close");
    const form = this.shadow.querySelector(".cm-form");
    const leadForm = this.shadow.querySelector(".cm-lead-form");

    launcher?.addEventListener("click", () => this.togglePanel(true));
    closeBtn?.addEventListener("click", () => this.togglePanel(false));
    form?.addEventListener("submit", (e) => {
      e.preventDefault();
      const input = this.shadow?.querySelector<HTMLInputElement>(".cm-input");
      const text = input?.value.trim();
      if (!text || this.streaming) return;
      input!.value = "";
      void this.sendMessage(text);
    });
    leadForm?.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.submitLead(leadForm as HTMLFormElement);
    });
  }

  private togglePanel(open: boolean): void {
    this.panelOpen = open;
    const root = this.shadow?.querySelector(".cm-root");
    const panel = this.shadow?.querySelector(".cm-panel");

    root?.classList.toggle("cm-open", open);
    panel?.classList.toggle("cm-visible", open);

    if (open && this.messages.length === 0) {
      this.messages.push({
        id: uuid(),
        role: "assistant",
        content: this.config.greeting,
        createdAt: new Date().toISOString(),
        isGreeting: true,
      });
      this.renderMessages();
    }
    if (open) {
      requestAnimationFrame(() => {
        this.shadow?.querySelector<HTMLInputElement>(".cm-input")?.focus();
      });
    }
  }

  private renderQuickActions(): void {
    const container = this.shadow?.querySelector(".cm-quick-actions");
    if (!container) return;

    const actions = this.quickActions.length
      ? this.quickActions
      : [
          { id: "book", label: "Book a call", message: "I'd like to book a call with Clearmind." },
          { id: "min_ticket", label: "Minimum investment", message: "What are the minimum investments for your products?" },
          { id: "compare", label: "Compare products", message: "Can you help me compare Polaris PMS, Optimus, and Polaris Lite?" },
          { id: "sebi", label: "SEBI registration", message: "What are Clearmind's SEBI registration numbers?" },
        ];

    if (this.messages.length > 1 || this.mode !== "ai_active") {
      container.innerHTML = "";
      return;
    }

    container.innerHTML = actions
      .map(
        (a) =>
          `<button type="button" class="cm-chip" data-msg="${escapeAttr(a.message)}" ${this.streaming ? "disabled" : ""}>${escapeHtml(a.label)}</button>`
      )
      .join("");

    container.querySelectorAll<HTMLButtonElement>(".cm-chip").forEach((btn) => {
      btn.addEventListener("click", () => {
        const msg = btn.dataset.msg;
        if (msg && !this.streaming) void this.sendMessage(msg);
      });
    });
  }

  private renderMessages(): void {
    const container = this.shadow?.querySelector(".cm-messages");
    if (!container) return;

    container.innerHTML = this.messages
      .map((m) => this.renderMessageHtml(m))
      .join("");

    container.querySelectorAll<HTMLButtonElement>("[data-feedback]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const messageId = btn.dataset.messageId;
        const rating = Number(btn.dataset.rating) as -1 | 1;
        if (messageId && (rating === 1 || rating === -1)) {
          void this.submitFeedback(messageId, rating);
        }
      });
    });

    const sendBtn = this.shadow?.querySelector<HTMLButtonElement>(".cm-send");
    if (sendBtn) sendBtn.disabled = this.streaming;

    this.renderQuickActions();
    container.scrollTop = container.scrollHeight;
  }

  private renderMessageHtml(m: ChatMessage): string {
    const isUser = m.role === "user";
    const isStreamingThis = this.streaming && m.id === this.streamingMessageId;
    const showTyping = isStreamingThis && !m.content.trim();

    const bubbleContent = showTyping
      ? `<span class="cm-typing" aria-label="Assistant is typing"><span></span><span></span><span></span></span>`
      : formatMarkdown(m.content);

    const sources =
      m.sources && m.sources.length
        ? `<div class="cm-sources">
            <span class="cm-sources-label">Sources</span>
            ${m.sources.map((s) => renderSourceChip(s)).join("")}
          </div>`
        : "";

    const showFeedback =
      !isUser && !m.isGreeting && !isStreamingThis && m.serverMessageId && m.content.trim();
    const feedback =
      showFeedback
        ? `<div class="cm-feedback" role="group" aria-label="Rate this answer">
            <button type="button" class="cm-feedback-btn ${m.feedbackRating === 1 ? "cm-active" : ""}" data-feedback="up" data-message-id="${escapeAttr(m.serverMessageId!)}" data-rating="1" aria-label="Helpful" ${m.feedbackRating ? "disabled" : ""}>${ICONS.thumbUp}</button>
            <button type="button" class="cm-feedback-btn ${m.feedbackRating === -1 ? "cm-active" : ""}" data-feedback="down" data-message-id="${escapeAttr(m.serverMessageId!)}" data-rating="-1" aria-label="Not helpful" ${m.feedbackRating ? "disabled" : ""}>${ICONS.thumbDown}</button>
          </div>`
        : "";

    if (isUser) {
      return `
        <div class="cm-msg cm-msg-user">
          <div class="cm-msg-inner">
            <div class="cm-msg-body">
              <div class="cm-bubble">${bubbleContent}</div>
            </div>
          </div>
        </div>`;
    }

    return `
      <div class="cm-msg cm-msg-assistant">
        <div class="cm-msg-inner">
          <div class="cm-avatar" aria-hidden="true">CM</div>
          <div class="cm-msg-body">
            <div class="cm-bubble">${bubbleContent}</div>
            ${sources}
            ${feedback}
          </div>
        </div>
      </div>`;
  }

  private async sendMessage(text: string): Promise<void> {
    this.messages.push({
      id: uuid(),
      role: "user",
      content: text,
      createdAt: new Date().toISOString(),
    });
    this.renderMessages();

    const assistantId = uuid();
    this.streamingMessageId = assistantId;
    this.messages.push({
      id: assistantId,
      role: "assistant",
      content: "",
      createdAt: new Date().toISOString(),
    });
    this.streaming = true;
    this.renderMessages();

    try {
      const res = await fetch(`${this.config.apiUrl}/api/v1/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site_key: this.config.siteKey,
          session_id: this.sessionId,
          message: text,
          source_page: typeof document === "undefined" ? undefined : document.referrer || window.location.href,
        }),
      });

      if (!res.ok || !res.body) {
        throw new Error(`Chat request failed (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      const assistant = this.messages.find((m) => m.id === assistantId);
      if (!assistant) return;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (!payload || payload === "[DONE]") continue;
          try {
            const event = JSON.parse(payload) as ChatStreamEvent;
            this.applyStreamEvent(assistant, event);
            this.renderMessages();
          } catch {
            // skip malformed SSE line
          }
        }
      }
      if (!assistant.content.trim()) {
        assistant.content = this.fallbackMessage();
      }
    } catch (err) {
      const assistant = this.messages.find((m) => m.id === assistantId);
      if (assistant) {
        assistant.content = this.fallbackMessage();
      }
      this.renderMessages();
      console.error("[ClearMindChat]", err);
    } finally {
      const assistant = this.messages.find((m) => m.id === assistantId);
      if (assistant && !assistant.content.trim()) {
        assistant.content = this.fallbackMessage();
      }
      this.streaming = false;
      this.streamingMessageId = null;
      this.renderMessages();
    }
  }

  private applyStreamEvent(msg: ChatMessage, event: ChatStreamEvent): void {
    switch (event.type) {
      case "meta":
        if (event.message_id) msg.serverMessageId = event.message_id;
        break;
      case "token":
        msg.content += event.content ?? "";
        break;
      case "sources":
        msg.sources = event.sources;
        break;
      case "decline":
      case "handoff":
      case "replace":
        msg.content = event.content ?? msg.content;
        if (event.sources) msg.sources = event.sources;
        if (event.type === "handoff" && event.reason !== "book_call") {
          this.mode = event.reason?.startsWith("conversation_resolved")
            ? "resolved"
            : event.reason?.startsWith("conversation_human_active")
              ? "human_active"
              : "waiting_for_human";
          this.startPolling();
        }
        break;
      case "error":
        msg.content = event.error?.trim() || this.fallbackMessage();
        break;
      case "done":
        break;
      default: {
        const _exhaustive: never = event.type;
        return _exhaustive;
      }
    }
  }

  private fallbackMessage(): string {
    return "I'm sorry, I couldn't complete that response right now. Please try again or [contact our team](https://www.iamclearmind.com/contact).";
  }

  private async submitFeedback(messageId: string, rating: -1 | 1): Promise<void> {
    const msg = this.messages.find((m) => m.serverMessageId === messageId);
    if (!msg || msg.feedbackRating) return;

    msg.feedbackRating = rating;
    this.renderMessages();

    try {
      const res = await fetch(`${this.config.apiUrl}/api/v1/chat/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site_key: this.config.siteKey,
          session_id: this.sessionId,
          message_id: messageId,
          rating,
        }),
      });
      if (!res.ok) {
        msg.feedbackRating = undefined;
        this.renderMessages();
      }
    } catch {
      msg.feedbackRating = undefined;
      this.renderMessages();
    }
  }

  private statusLabel(): string {
    switch (this.mode) {
      case "ai_active":
        return "Typically replies instantly";
      case "waiting_for_human":
        return "Team requested";
      case "human_active":
        return "A team member is replying";
      case "resolved":
        return "Conversation resolved";
      default: {
        const exhaustive: never = this.mode;
        return exhaustive;
      }
    }
  }

  private renderLeadCapture(): string {
    if (this.mode !== "waiting_for_human" && !this.leadSaved) return "";
    if (this.leadSaved) {
      return `<div class="cm-lead-saved" role="status">✓ Details shared. The team can continue from this conversation.</div>`;
    }
    return `
      <form class="cm-lead-form">
        <strong>Want a callback?</strong>
        <p>Share your details once; the team will continue from this chat.</p>
        <input name="name" required maxlength="120" placeholder="Full name" autocomplete="name" />
        <input name="email" required type="email" maxlength="254" placeholder="Email address" autocomplete="email" />
        <input name="mobile" required maxlength="32" placeholder="Mobile number" autocomplete="tel" />
        <label><input name="consent" required type="checkbox" /> I agree that ClearMind may contact me about this enquiry.</label>
        ${this.leadError ? `<p class="cm-lead-error" role="alert">${escapeHtml(this.leadError)}</p>` : ""}
        <button type="submit" ${this.leadSubmitting ? "disabled" : ""}>${this.leadSubmitting ? "Sharing…" : "Share with the team"}</button>
      </form>`;
  }

  private async submitLead(form: HTMLFormElement): Promise<void> {
    if (this.leadSubmitting || this.leadSaved) return;
    const data = new FormData(form);
    this.leadSubmitting = true;
    this.leadError = "";
    this.renderShell();
    try {
      const response = await fetch(`${this.config.apiUrl}/api/v1/chat/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site_key: this.config.siteKey,
          session_id: this.sessionId,
          name: data.get("name"),
          email: data.get("email"),
          mobile: data.get("mobile"),
          consent: data.get("consent") === "on",
        }),
      });
      const payload = (await response.json()) as { detail?: string };
      if (!response.ok) throw new Error(payload.detail ?? "Unable to share those details");
      this.leadSaved = true;
    } catch (error) {
      this.leadError = error instanceof Error ? error.message : "Unable to share those details";
    } finally {
      this.leadSubmitting = false;
      this.renderShell();
    }
  }

  private startPolling(): void {
    if (this.pollTimer !== null) return;
    const poll = async () => {
      try {
        const response = await fetch(
          `${this.config.apiUrl}/api/v1/chat/conversations/${encodeURIComponent(this.config.siteKey)}/${encodeURIComponent(this.sessionId)}`,
          { cache: "no-store" },
        );
        if (!response.ok) return;
        const data = (await response.json()) as {
          mode: "ai_active" | "waiting_for_human" | "human_active" | "resolved";
          messages: { id: string; content: string; created_at: string }[];
        };
        this.mode = data.mode;
        const known = new Set(this.messages.map((message) => message.id));
        for (const message of data.messages) {
          const id = `admin-${message.id}`;
          if (!known.has(id)) {
            this.messages.push({
              id,
              role: "assistant",
              content: message.content,
              createdAt: message.created_at,
            });
          }
        }
        this.renderShell();
        if (this.mode === "ai_active" || this.mode === "resolved") this.stopPolling();
      } catch {
        // Polling is best effort; the local transcript remains usable offline.
      }
    };
    void poll();
    this.pollTimer = window.setInterval(poll, 3_000);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}

function renderSourceChip(s: SourceCitation): string {
  const title = truncate(s.title, 42);
  return `<a href="${escapeAttr(s.url)}" target="_blank" rel="noopener noreferrer" class="cm-source" title="${escapeAttr(s.title)}">${ICONS.external}<span>${escapeHtml(title)}</span></a>`;
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/'/g, "&#39;");
}

function formatMarkdown(text: string): string {
  const blocks = escapeHtml(text).split(/\n{2,}/);
  return blocks
    .filter((block) => block.trim())
    .map((block) => {
      const lines = block.split("\n");
      if (lines.every((line) => /^\s*[-*]\s+/.test(line))) {
        return `<ul>${lines
          .map((line) => `<li>${formatInlineMarkdown(line.replace(/^\s*[-*]\s+/, ""))}</li>`)
          .join("")}</ul>`;
      }
      if (lines.every((line) => /^\s*\d+[.)]\s+/.test(line))) {
        return `<ol>${lines
          .map((line) => `<li>${formatInlineMarkdown(line.replace(/^\s*\d+[.)]\s+/, ""))}</li>`)
          .join("")}</ol>`;
      }
      return `<p>${formatInlineMarkdown(lines.join("<br/>"))}</p>`;
    })
    .join("");
}

function formatInlineMarkdown(escapedText: string): string {
  let html = escapedText;
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  return html;
}

function autoInit(): void {
  const widget = new ClearMindChatWidget();
  widget.mount();
  (window as Window & { ClearMindChat?: ClearMindChatWidget }).ClearMindChat = widget;
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit);
  } else {
    autoInit();
  }
}

export default ClearMindChatWidget;
